import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Download, FileSpreadsheet, Save, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "@/components/feedback/ToastHost";
import { EmptyState } from "@/components/feedback/EmptyState";
import { errorMessage } from "@/lib/errors";
import {
  createSavedReport,
  deleteSavedReport,
  exportReportUrl,
  listSavedReports,
  runReport,
  type SavedReport,
} from "@/services/api/reports";

const LOAD_STATUSES = ["draft", "locked", "booked", "dispatched", "completed"];

const REPORT_TYPES: { value: string; label: string }[] = [
  { value: "outstanding_invoices", label: "Outstanding invoices" },
  { value: "loads", label: "Loads" },
  { value: "haulier_activity", label: "Haulier activity" },
  { value: "bay_utilization", label: "Bay utilization" },
  { value: "import_health", label: "Import health" },
  { value: "audit_extract", label: "Audit extract" },
];

/** Operational reporting: filterable result tables with streaming CSV/XLSX export. */
export function ReportsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [reportType, setReportType] = useState("loads");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [status, setStatus] = useState("");
  const [saveName, setSaveName] = useState("");

  const filters = { date_from: dateFrom, date_to: dateTo, status };

  const report = useQuery({
    queryKey: ["reports", reportType, dateFrom, dateTo, status],
    queryFn: () => runReport(reportType, filters),
  });
  const saved = useQuery({
    queryKey: ["reports", "saved"],
    queryFn: () => listSavedReports(),
  });

  const refreshSaved = () =>
    void queryClient.invalidateQueries({ queryKey: ["reports", "saved"] });

  const saveReport = useMutation({
    mutationFn: () =>
      createSavedReport({ name: saveName, report_type: reportType, filters }),
    onSuccess: () => {
      toast.success(t("admin.saved"));
      setSaveName("");
      refreshSaved();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const removeSaved = useMutation({
    mutationFn: (id: number) => deleteSavedReport(id),
    onSuccess: () => refreshSaved(),
    onError: (error) => toast.error(errorMessage(error)),
  });

  const loadSaved = (item: SavedReport) => {
    setReportType(item.report_type);
    setDateFrom(String(item.filters.date_from ?? ""));
    setDateTo(String(item.filters.date_to ?? ""));
    setStatus(String(item.filters.status ?? ""));
  };

  const columns = report.data?.columns ?? [];
  const rows = report.data?.rows ?? [];
  const exportFilters = {
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    status: status || undefined,
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">{t("nav.reports")}</h1>
        <p className="text-sm text-muted-foreground">
          Operational reports with server-side filters and streaming exports.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <div className="space-y-2">
          <Label>{t("admin.reportType")}</Label>
          <select
            value={reportType}
            onChange={(e) => setReportType(e.target.value)}
            className="h-9 w-full rounded-md border bg-background px-3 text-sm"
          >
            {REPORT_TYPES.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-2">
          <Label>{t("admin.dateFrom")}</Label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        </div>
        <div className="space-y-2">
          <Label>{t("admin.dateTo")}</Label>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </div>
        <div className="space-y-2">
          <Label>{t("admin.status")}</Label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="h-9 w-full rounded-md border bg-background px-3 text-sm"
          >
            <option value="">—</option>
            {LOAD_STATUSES.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" onClick={() => void report.refetch()} disabled={report.isFetching}>
          {t("admin.runReport")}
        </Button>
        <a href={exportReportUrl(reportType, "csv", exportFilters)} download>
          <Button variant="outline" type="button">
            <Download className="h-4 w-4" /> {t("admin.exportCsv")}
          </Button>
        </a>
        <a href={exportReportUrl(reportType, "xlsx", exportFilters)} download>
          <Button variant="outline" type="button">
            <FileSpreadsheet className="h-4 w-4" /> {t("admin.exportXlsx")}
          </Button>
        </a>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((column) => (
                <TableHead key={column.key}>{column.label}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {report.isLoading ? (
              <TableRow>
                <TableCell colSpan={Math.max(columns.length, 1)}>
                  <Skeleton className="h-4 w-full" />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={Math.max(columns.length, 1)} className="py-8">
                  <EmptyState title={t("admin.noResults")} />
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, rowIndex) => (
                <TableRow key={rowIndex}>
                  {columns.map((column) => (
                    <TableCell key={column.key}>
                      {String(row[column.key] ?? "—")}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <div className="rounded-md border p-4">
        <Label className="text-sm font-semibold">{t("admin.savedReports")}</Label>
        <div className="mt-3 flex gap-2">
          <Input
            value={saveName}
            onChange={(e) => setSaveName(e.target.value)}
            placeholder={t("admin.reportName")}
            className="max-w-xs"
          />
          <Button
            onClick={() => saveReport.mutate()}
            disabled={!saveName.trim() || saveReport.isPending}
          >
            <Save className="h-4 w-4" /> {t("admin.saveReport")}
          </Button>
        </div>
        <div className="mt-3 space-y-1">
          {(saved.data ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("admin.noResults")}</p>
          ) : (
            (saved.data ?? []).map((item) => (
              <div
                key={item.id}
                className="flex items-center justify-between rounded border px-3 py-2 text-sm"
              >
                <button
                  type="button"
                  className="text-left hover:underline"
                  onClick={() => loadSaved(item)}
                >
                  <span className="font-medium">{item.name}</span>
                  <span className="ml-2 text-muted-foreground">{item.report_type}</span>
                </button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-destructive hover:text-destructive"
                  onClick={() => removeSaved.mutate(item.id)}
                  aria-label={t("common.delete")}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
